import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api.js';
import { extractPaletteFromImage } from '../lib/colorExtract.js';
import UploadHint from './UploadHint.jsx';
import EmailPreviewFrame from './EmailPreviewFrame.jsx';

// Editable email branding/template with a live preview. Used at two scopes:
//   • platform  (entityId omitted) — Howler's default for all notifications
//   • client    (entityId set)     — a client's own branding, layered on top
// Blank fields inherit the tier below, so the placeholder shows what will be
// used if left empty. The HTML shell + "Powered by Howler Pulse" are fixed.
const FIELDS = [
  ['senderName', 'Sender name', 'text', 'Shown on the From line, e.g. "Kunye"'],
  ['brandColor', 'Primary colour', 'color', 'Buttons, accents, first chart series — across the platform and emails'],
  ['secondaryColor', 'Secondary colour', 'color', 'Gradient partner and second chart series'],
  ['chart3', 'Chart colour 3', 'color', 'Third chart series'],
  ['chart4', 'Chart colour 4', 'color', 'Fourth chart series'],
  ['chart5', 'Chart colour 5', 'color', 'Fifth chart series — further series are generated from your palette'],
  ['logo', 'Logo image', 'logo', 'Shown across your Pulse app (sidebar + identity) and at the top of your emails. Upload an image or paste a URL — blank uses the wordmark'],
  ['logoDark', 'Dark-mode logo', 'logoDark', 'Optional — a light/white version of your logo for dark mode in the app. Leave blank and we show your normal logo on a light chip so it stays visible. Emails always use the logo above'],
  ['wordmark', 'Wordmark', 'text', 'Text shown if there is no logo'],
  ['header', 'Header text', 'textarea', 'Tagline shown under the logo, at the top of every email'],
  ['intro', 'Intro line', 'textarea', 'Optional line above the message, inside the card'],
  ['footer', 'Footer text', 'textarea', 'Small print under the card — supports multiple lines (e.g. contact details)'],
  ['metricScale', 'Metric number size', 'scale', 'How big KPI numbers render on your dashboards (100% = default). Affects the big single-value tiles only'],
];

export default function MailTemplateEditor({ entityId, scope = 'platform', suiteId = '', canTest = false }) {
  const [data, setData] = useState(null);     // { template|branding, resolved?, defaults }
  const [edits, setEdits] = useState({});     // local overrides being edited
  const [previewHtml, setPreviewHtml] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testState, setTestState] = useState('');
  const debounce = useRef(null);

  const loadFn = scope === 'platform' ? () => api.getMailTemplate()
    : scope === 'admin-suite' ? () => api.getSuiteMailTemplate(suiteId)
    : scope === 'my-suite' ? () => api.getMySuiteMailTemplate(suiteId)
    : scope === 'admin-client' ? () => api.getEntityMailTemplate(entityId)
    : () => api.getMyMailTemplate(entityId);
  const saveFn = scope === 'platform' ? (p) => api.saveMailTemplate(p)
    : scope === 'admin-suite' ? (p) => api.saveSuiteMailTemplate(suiteId, p)
    : scope === 'my-suite' ? (p) => api.saveMySuiteMailTemplate(suiteId, p)
    : scope === 'admin-client' ? (p) => api.saveEntityMailTemplate(entityId, p)
    : (p) => api.saveMyMailTemplate(entityId, p);

  useEffect(() => {
    loadFn().then((d) => { setData(d); setEdits({ ...(d.template || d.branding || {}) }); });
  }, [entityId, scope, suiteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced live preview as the user types.
  useEffect(() => {
    if (!data) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      api.previewMail(edits, entityId, suiteId).then((r) => setPreviewHtml(r.html)).catch(() => {});
    }, 350);
    return () => clearTimeout(debounce.current);
  }, [edits, entityId, suiteId, data]);

  if (!data) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</p>;
  const placeholderFor = (k) => (scope === 'platform' ? data.defaults?.[k] : data.resolved?.[k]) || '';
  const set = (k, v) => setEdits((e) => ({ ...e, [k]: v }));

  async function save() {
    setBusy(true);
    try {
      const d = await saveFn(edits); setData(d); setSaved(true); setTimeout(() => setSaved(false), 1600);
      // Tell the app shell to re-pull the theme so the logo/colours update live
      // (no reload). Platform scope has no entityId — skip. A suite edit carries
      // the suiteId so the shell re-themes when viewing that event.
      if (entityId || suiteId) window.dispatchEvent(new CustomEvent('pulse-branding-saved', { detail: { entityId, suiteId } }));
    }
    catch (e) { alert('Save failed: ' + e.message); }
    finally { setBusy(false); }
  }

  // Fill the five colour slots from the logo's dominant colours (most prominent
  // first). Unsaved — the user can tweak each before hitting Save.
  const applyExtracted = (hexes) => setEdits((e) => {
    const slots = ['brandColor', 'secondaryColor', 'chart3', 'chart4', 'chart5'];
    const next = { ...e };
    // Fill what the logo gave us; clear the rest so they fall back to the
    // palette generator instead of stale manual picks.
    slots.forEach((s, i) => { next[s] = hexes[i] || ''; });
    return next;
  });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 20, alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {FIELDS.map(([key, label, type, help]) => (
          <div key={key} data-tour={`mte-${key}`}>
            <div style={lbl}>{label}</div>
            {type === 'logo' ? (
              <LogoField value={edits[key] || ''} inherited={placeholderFor(key)} onChange={(v) => set(key, v)} onExtract={applyExtracted} />
            ) : type === 'logoDark' ? (
              <LogoField value={edits[key] || ''} inherited={placeholderFor(key)} onChange={(v) => set(key, v)} dark />
            ) : type === 'textarea' ? (
              <textarea value={edits[key] || ''} onChange={(e) => set(key, e.target.value)} placeholder={placeholderFor(key)} rows={2} style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }} />
            ) : type === 'scale' ? (
              (() => {
                const isSet = edits[key] !== '' && edits[key] != null;
                const cur = isSet ? Number(edits[key]) : (Number(placeholderFor(key)) || 1);
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="range" min="0.7" max="1.4" step="0.05" value={cur} onChange={(e) => set(key, e.target.value)} style={{ flex: 1 }} />
                    <span style={{ width: 44, textAlign: 'right', fontWeight: 700, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{Math.round(cur * 100)}%</span>
                    {isSet && <button type="button" onClick={() => set(key, '')} style={{ border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--muted)', borderRadius: 980, fontSize: 11.5, fontWeight: 600, padding: '4px 9px', cursor: 'pointer' }}>Reset</button>}
                  </div>
                );
              })()
            ) : type === 'color' ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={/^#[0-9a-f]{6}$/i.test(edits[key] || '') ? edits[key] : (placeholderFor(key) || '#FF385C')} onChange={(e) => set(key, e.target.value)} style={{ width: 44, height: 34, border: '1px solid var(--hairline)', borderRadius: 8, padding: 2, cursor: 'pointer' }} />
                <input value={edits[key] || ''} onChange={(e) => set(key, e.target.value)} placeholder={placeholderFor(key)} style={{ ...input, flex: 1 }} />
              </div>
            ) : (
              <input value={edits[key] || ''} onChange={(e) => set(key, e.target.value)} placeholder={placeholderFor(key)} style={input} />
            )}
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>{help}{(edits[key] || '') === '' ? ' · inheriting' : ''}</div>
          </div>
        ))}
        <div style={{ fontSize: 11.5, color: 'var(--muted)', background: 'var(--elevated, #f7f7f8)', border: '1px solid var(--hairline)', borderRadius: 8, padding: '8px 10px' }}>
          The primary + secondary pair drives the look of the whole platform (buttons, accents, chart palettes) and these emails. Sends stay on Howler's verified domain, and a “Powered by Howler : Pulse” line stays in the footer.
        </div>
        <div data-tour="mte-save" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
          <button style={saveBtn} onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          {saved && <span style={{ color: 'var(--success, #10b981)', fontSize: 13, fontWeight: 600 }}>✓ Saved</span>}
          {canTest && (
            <button
              type="button"
              style={{ ...saveBtn, background: 'rgba(128,128,128,0.14)', color: 'var(--text)' }}
              disabled={testState === 'sending'}
              onClick={async () => { setTestState('sending'); try { const r = await api.sendMailTest(entityId); setTestState(`✓ Sent to ${r.to}`); } catch (e) { setTestState(`✗ ${e.message}`); } }}
            >{testState === 'sending' ? 'Sending…' : 'Send me a test'}</button>
          )}
          {testState && testState !== 'sending' && <span style={{ fontSize: 12, color: testState.startsWith('✓') ? 'var(--success,#10b981)' : 'var(--error,#ef4444)' }}>{testState}</span>}
        </div>
      </div>

      <div data-tour="mte-preview">
        <div style={lbl}>Live preview</div>
        <EmailPreviewFrame html={previewHtml} height={460} />
      </div>
    </div>
  );
}

// Logo: upload an image (resized client-side to ≤320px, stored as a data-URL —
// emails reference it via /mail-assets/logo/:scope since Gmail strips data-URLs)
// or paste a hosted URL. Shows the inherited logo when nothing is set here.
function LogoField({ value, inherited, onChange, onExtract, dark = false }) {
  const fileRef = useRef(null);
  const shown = value || inherited;
  const [extractState, setExtractState] = useState('');
  const extract = async () => {
    if (!shown) return;
    setExtractState('working');
    try {
      const hexes = await extractPaletteFromImage(shown, 5);
      onExtract?.(hexes);
      setExtractState(`✓ ${hexes.length} colour${hexes.length === 1 ? '' : 's'} applied — tweak & Save`);
    } catch (e) { setExtractState(`✗ ${e.message}`); }
  };
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 320, scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const c = document.createElement('canvas'); c.width = w; c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        onChange(c.toDataURL('image/png'));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
    e.target.value = ''; // allow re-uploading the same file
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <div style={{ width: 120, height: 44, border: '1px dashed var(--hairline)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: dark ? '#1c1c1e' : '#fff' }}>
          {shown ? <img src={shown} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', opacity: value ? 1 : 0.45 }} /> : <span style={{ fontSize: 11, color: dark ? '#8e8e93' : 'var(--muted)' }}>No logo</span>}
        </div>
        <button type="button" style={smallBtn} onClick={() => fileRef.current?.click()}>Upload image</button>
        {shown && onExtract && (
          <button type="button" style={smallBtn} onClick={extract} disabled={extractState === 'working'}>
            {extractState === 'working' ? 'Reading…' : '🎨 Extract colours'}
          </button>
        )}
        {value && <button type="button" style={{ ...smallBtn, background: 'transparent', color: 'var(--error, #ef4444)' }} onClick={() => onChange('')}>Remove</button>}
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
      </div>
      {extractState && extractState !== 'working' && (
        <div style={{ fontSize: 11.5, color: extractState.startsWith('✓') ? 'var(--success, #10b981)' : 'var(--error, #ef4444)' }}>{extractState}</div>
      )}
      {!value?.startsWith('data:') && (
        <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={inherited && !inherited.startsWith('data:') ? inherited : 'or paste an image URL: https://…/logo.png'} style={input} />
      )}
      <UploadHint kind="logo" text={dark ? 'PNG or SVG, transparent background — a light/white version made to sit on a dark header. Square at least 512×512px, or a landscape lockup up to about 1200px wide. Under 2MB. Optional: blank falls back to your normal logo on a light chip.' : undefined} />
    </div>
  );
}

const smallBtn = { border: '1px solid var(--hairline)', background: 'rgba(128,128,128,0.10)', color: 'var(--text)', borderRadius: 980, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const lbl = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', margin: '0 0 5px' };
const input = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1.5px solid var(--hairline)', borderRadius: 8, fontSize: 13, outline: 'none', background: 'var(--card)', color: 'var(--text)' };
const saveBtn = { padding: '9px 18px', background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: 980, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
